import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { errorMessage } from "../api/errors";
import { MOCK_PASSWORD } from "../mocks/db";
import { mocksEnabled } from "../lib/env";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";
import { useToast } from "../components/ui/Toast";

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  // Demo hesabı yalnızca mock modda ön dolu gelir; gerçek kurulumda boş başlar.
  const [email, setEmail] = useState(mocksEnabled() ? "admin@demo.local" : "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from =
    (location.state as { from?: string } | null)?.from ?? "/";

  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast(`Hoş geldiniz, ${user.displayName ?? user.email}`, "success");
      navigate(from, { replace: true });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span
            aria-hidden="true"
            className="bg-primary text-primary-foreground mx-auto flex h-12 w-12 items-center justify-center rounded-xl text-lg font-bold"
          >
            N
          </span>
          <h1 className="mt-3 text-xl font-semibold">İzleme Platformu</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Devam etmek için oturum açın
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Oturum aç</CardTitle>
            <CardDescription>
              Panel hesabı e-posta ve parolanızla giriş yapın.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Input
                label="E-posta"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <Input
                label="Parola"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {error ? (
                <p role="alert" className="bg-danger/10 text-danger rounded-md border border-danger/30 px-3 py-2 text-sm">
                  {error}
                </p>
              ) : null}
              <Button type="submit" disabled={submitting}>
                {submitting ? "Giriş yapılıyor…" : "Giriş yap"}
              </Button>
            </form>

            {mocksEnabled() ? (
              <div className="bg-muted/60 text-muted-foreground mt-5 rounded-md border border-border p-3 text-xs">
                <p className="font-medium text-foreground">Demo kimlik bilgileri (mock)</p>
                <ul className="mt-1 space-y-0.5">
                  <li>admin@demo.local · {MOCK_PASSWORD} — yönetici</li>
                  <li>operator@demo.local · {MOCK_PASSWORD} — operatör</li>
                  <li>viewer@demo.local · {MOCK_PASSWORD} — görüntüleyici</li>
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}